import { Link } from "react-router-dom";
import { Fingerprint, ArrowLeft } from "lucide-react";

function Section({ title, children }) {
  return (
    <section className="space-y-2">
      <h2 className="font-serif-dl text-2xl tracking-tight text-[#1A1A1A]">{title}</h2>
      <div className="text-[#5C5A54] leading-relaxed space-y-3">{children}</div>
    </section>
  );
}

export default function Privacy() {
  return (
    <div className="min-h-screen paper-texture" data-testid="privacy-page">
      <header className="max-w-3xl mx-auto px-6 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="privacy-brand-link">
          <Fingerprint className="text-[#1B4D3E]" size={24} strokeWidth={1.6} />
          <span className="font-serif-dl text-xl font-semibold tracking-tight">Dap Ledger</span>
        </Link>
        <Link
          to="/"
          data-testid="privacy-back-link"
          className="flex items-center gap-2 text-[#5C5A54] hover:text-[#1A1A1A] text-sm transition-colors"
        >
          <ArrowLeft size={15} /> Back
        </Link>
      </header>

      <main className="max-w-3xl mx-auto px-6 pb-24">
        <p className="overline text-[#E07A5F]">Legal</p>
        <h1 className="font-serif-dl text-4xl sm:text-5xl tracking-tight mt-2">Privacy Policy</h1>
        <p className="text-sm text-[#8A877F] mt-3">Last updated: June 2026</p>

        <div className="mt-6 rounded-md bg-[#F0EBE1] border border-[#DEDBD3] px-4 py-3 text-sm text-[#8f6b52]">
          This is placeholder text provided as a template. Please have it reviewed by
          qualified legal counsel before relying on it in production.
        </div>

        <div className="mt-10 space-y-10">
          <Section title="1. Overview">
            <p>
              This Privacy Policy explains how Dap Ledger (“we,” “us”) collects, uses, and
              protects your information when you use our Service. By using the Service, you agree
              to the practices described here.
            </p>
          </Section>

          <Section title="2. Information We Collect">
            <p>
              We collect information you provide directly — such as your name, email address, and
              optional phone number — as well as the agreements (“daps”) you create and their
              status. We also generate cryptographic keys on your device to seal agreements; your
              private signing key never leaves your device.
            </p>
          </Section>

          <Section title="3. How We Use Information">
            <p>
              We use your information to operate the Service: to create and seal agreements, send
              notifications, maintain the tamper-evident ledger, calculate your private honor
              standing, and keep the Service secure.
            </p>
          </Section>

          <Section title="4. Sharing & Disclosure">
            <p>
              Your standing is private by default. We do not sell your personal information. The
              details of an agreement are visible only to its parties and, where applicable, an
              invited mediator. We may share information with service providers who help us run the
              Service, or when required by law.
            </p>
          </Section>

          <Section title="5. Notifications">
            <p>
              If you enable push notifications, we store a browser push subscription so we can
              alert you about your daps. You can disable notifications at any time from your device
              or settings.
            </p>
          </Section>

          <Section title="6. Data Retention">
            <p>
              We retain your information for as long as your account is active or as needed to
              provide the Service. Ledger records are designed to be tamper-evident and may persist
              to preserve the integrity of the chain.
            </p>
          </Section>

          <Section title="7. Security">
            <p>
              We use reasonable technical and organizational measures to protect your information,
              including non-extractable on-device signing keys and signed, chained ledger records.
              No method of transmission or storage is completely secure, however.
            </p>
          </Section>

          <Section title="8. Your Rights">
            <p>
              Depending on your location, you may have rights to access, correct, or delete your
              personal information, or to object to certain processing. Contact us to exercise
              these rights.
            </p>
          </Section>

          <Section title="9. Children’s Privacy">
            <p>
              The Service is not directed to children under 18, and we do not knowingly collect
              personal information from them.
            </p>
          </Section>

          <Section title="10. Changes to This Policy">
            <p>
              We may update this Privacy Policy from time to time. When we do, we will revise the
              “Last updated” date above.
            </p>
          </Section>

          <Section title="11. Contact">
            <p>
              Questions about your privacy? Contact us at{" "}
              <span className="text-[#1B4D3E]">support@thedapledger.com</span>.
            </p>
          </Section>
        </div>
      </main>
    </div>
  );
}
